// «Ушёл в другое окно, вернулся — у иконки висит подпись, а курсор далеко»
// (owner, 25.08.2026).
//
// Hover is only ever ended by a `mouseleave`, and a tab that is not on screen
// receives no pointer events at all: alt-tab away from a page while the cursor
// rests on the launcher and the browser has nothing to send: the caption stays
// unfolded, the user moves the mouse somewhere else entirely in another window,
// and on return the plate is still standing there with no cursor near it.
//
// So the widget has to notice the departure itself. Two signals, because they
// cover different halves and neither covers both: switching TABS fires
// `visibilitychange` while the window keeps its focus, and switching to another
// WINDOW (or another app) fires `blur` on a window whose tab is still «visible».
//
// `window` / `document` behind a web gate (I-2); inert on native, where the
// widget is a different component with no hover to leak.
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/**
 * Calls `onAway` when the page stops being what the user is looking at.
 *
 * The callback is kept in a ref, so the caller can pass a fresh closure every
 * render without re-subscribing (and without needing a `useCallback` of its own).
 */
export function usePageAway(onAway: () => void): void {
  const callback = useRef(onAway);
  useEffect(() => { callback.current = onAway; }, [onAway]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const onWindowBlur = () => callback.current();
    const onVisibilityChange = () => { if (document.hidden) callback.current(); };
    window.addEventListener('blur', onWindowBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('blur', onWindowBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
